import { OrganizationUserStatus } from '../contracts/organization-members';
import { OrganizationRole } from '../contracts/organizations';
/**
 * Counts active owners in an organization.
 * Only counts users with authStatus="active" who have the Owner role.
 * Excludes both invited and disabled owners - only active owners count.
 *
 * This ensures consistency between frontend and backend when validating
 * business rules like "cannot demote the last owner".
 *
 * @param members - Array of organization members
 * @returns Number of active owners in the organization
 *
 * @example
 * ```typescript
 * const members = [
 *   { id: '1', roles: ['owner'], authStatus: 'active' },
 *   { id: '2', roles: ['owner'], authStatus: 'invited' },
 *   { id: '3', roles: ['owner'], authStatus: 'disabled' },
 * ];
 * const count = countActiveOwners(members); // Returns 1
 * ```
 */
export function countActiveOwners(members) {
    return members.filter(member => {
        // Only count active owners (exclude invited and disabled users)
        if (member.authStatus !== OrganizationUserStatus.Active) {
            return false; // Invited/disabled owners don't count
        }
        return member.roles.some(role => role === OrganizationRole.Owner);
    }).length;
}
/**
 * Checks if a user can modify a target member based on roles.
 * Shared permission logic for both role changes and member removal.
 *
 * Rules:
 * - Only owners and managers can modify members
 * - Manager cannot modify owner (unless user is also owner)
 *
 * @param currentUserRoles - Roles of the user attempting the modification
 * @param targetMemberRoles - Roles of the member being modified
 * @returns true if modification is allowed, false otherwise
 *
 * @example
 * ```typescript
 * const canModify = canModifyMember(['manager'], ['owner']); // false
 * const canModify = canModifyMember(['owner'], ['owner']); // true
 * const canModify = canModifyMember(['manager'], ['manager']); // true
 * ```
 */
export function canModifyMember(currentUserRoles, targetMemberRoles) {
    const normalizedCurrentRoles = currentUserRoles.map(r => r.toLowerCase());
    const normalizedTargetRoles = targetMemberRoles.map(r => r.toLowerCase());
    const isCurrentUserOwner = normalizedCurrentRoles.includes(OrganizationRole.Owner.toLowerCase());
    const isCurrentUserManager = normalizedCurrentRoles.includes(OrganizationRole.Manager.toLowerCase());
    const isTargetOwner = normalizedTargetRoles.includes(OrganizationRole.Owner.toLowerCase());
    const isTargetSupport = normalizedTargetRoles.includes(OrganizationRole.Support.toLowerCase());
    // Only owners and managers can modify members
    if (!isCurrentUserOwner && !isCurrentUserManager) {
        return false;
    }
    // Manager cannot modify owner or support users — only owners can
    if (isCurrentUserManager &&
        !isCurrentUserOwner &&
        (isTargetOwner || isTargetSupport)) {
        return false;
    }
    return true;
}
//# sourceMappingURL=organization-members.js.map